import _ from "lodash";
import React from "react";
import moment from "moment";
import classNames from "classnames";

import {
  getTimeSince,
  getCommentDisplayContent,
  getStyleStrippedText,
} from "Utils";
import Unveil from "UIComponents/Unveil";
import ChatbarWrapper from "UIComponents/ChatbarWrapper";
import { AttachmentList } from "Attachment";
import {
  Dropdown,
  IconButton,
  DropdownMenu,
  Avatar,
  Tooltip,
} from "@toddle-edu/ds-web";
import { DotsHorizontalOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";

import classes from "./CommentItem.scss";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

const MENU_ITEMS = t => [
  {
    label: t("edit"),
    key: "EDIT",
  },
  {
    label: t("delete"),
    key: "DELETE",
    isDestructive: true,
  },
];

const CommentItem = ({
  commentData,
  isRightPaneChat,
  userId,
  handleClickDelete,
  canCreateAudioComment,
  disableTagging,
  collaborators,
  placeholder,
  parentType,
  mode,
  handleClickEdit,
  editCommentId,
  editComment,
  getCommentItemRef,
  chatBarScrollParent,
  testId,
}) => {
  const { t } = useI18n(["common"]);

  const {
    id: commentId,
    item: { attachments },
    createdAt,
    createdBy: { id, firstName, lastName, profileImage },
    label,
  } = commentData;

  const dateTextClasses = classNames({
    [classes.dateText]: true,
    ["text-body-3"]: true,
  });

  const menuItems = userId === id ? MENU_ITEMS(t) : [];

  const onMenuItemClick = action => {
    switch (action.key) {
      case "EDIT":
        handleClickEdit({ commentId });
        break;

      case "DELETE":
        handleClickDelete({ commentId });
        break;

      default:
        break;
    }
  };
  const isEditComment = editCommentId === commentId;
  const containerClasses = classNames(classes.container, {
    [classes.editCommentContainer]: isEditComment,
  });

  const cancelEditComment = () => {
    handleClickEdit({ commentId: null });
  };

  const onUpdateComment = params => {
    cancelEditComment();
    editComment(params);
  };

  const gridTemplateColumns = isRightPaneChat
    ? "repeat(1,1fr)"
    : _.get(attachments, "length", 0) > 1
    ? "repeat(auto-fill, minmax(92px, 1fr))"
    : "repeat(1,1fr)";

  const { createdBy } = commentData;
  const name = getFormattedUserName({
    user: createdBy,
  });

  return (
    <div className={containerClasses} ref={ref => getCommentItemRef(ref)}>
      <div className={classes.profile}>
        <Avatar
          name={name}
          src={profileImage}
          id={id}
          size="medium"
          shade="light"
        />
      </div>
      <div className={classes.rightContainer}>
        <div className={classes.header}>
          <div className={classes.nameDateWrapper}>
            <Tooltip tooltip={name}>
              <div
                className={classNames("text-label-3", "truncate", classes.name)}
              >
                {name}
              </div>
            </Tooltip>
            <div className={dateTextClasses}>
              <div className={classes.dot} />
              {getTimeSince(moment(createdAt), t)}
            </div>
          </div>
          {mode === "edit" ? (
            <div className={classes.actions}>
              {_.size(menuItems) > 0 && !isEditComment ? (
                <Dropdown
                  disabled={_.includes(commentId, "NEW")}
                  overlay={
                    <DropdownMenu
                      options={menuItems}
                      onClick={onMenuItemClick}
                      testId={`${testId}-${commentId}-actionMenu`}
                    />
                  }
                  placement="bottomRight"
                >
                  <IconButton
                    variant={"plain"}
                    size="xx-small"
                    icon={<DotsHorizontalOutlined />}
                    testId={`${testId}-${commentId}-actionMenu`}
                  />
                </Dropdown>
              ) : null}
            </div>
          ) : null}
        </div>
        {isEditComment ? (
          <div className={classes.chatBarWrapper}>
            <ChatbarWrapper
              messageData={{
                id: commentId,
                label,
                attachments,
              }}
              sendMessage={onUpdateComment}
              isTaggingEnabled={!disableTagging}
              collaborators={collaborators}
              parentType={parentType}
              inputTextPlaceHolder={placeholder}
              showAddButton={false}
              isAudioRecordingEnabled={canCreateAudioComment}
              cancelEditMessage={cancelEditComment}
              mode={mode}
              shouldExecuteMessageDataUseEffect={false}
              chatBarScrollParent={chatBarScrollParent}
              testIdPrefix={`${testId}-${commentId}`}
            />
          </div>
        ) : (
          <div>
            <div className={classes.textAttachmentsWrapper}>
              <Unveil maxHeight={104} showCollapse={true}>
                <div
                  className={classes.textContainer}
                  dangerouslySetInnerHTML={{
                    __html: getStyleStrippedText({
                      text: getCommentDisplayContent(label),
                      type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                    }),
                  }}
                />
              </Unveil>
              <AttachmentList
                value={attachments}
                cardType={"vertical"}
                mode={"view"}
                cardStyle={{ height: 80 }}
                attachmentContainerStyle={{
                  gridTemplateColumns,
                  gridGap: "12px",
                  marginTop: "4px",
                  position: "relative",
                }}
                clubbingCards={2}
                showSingleCardTheme={true}
                isRightPaneChat={isRightPaneChat}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentItem;
